import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { CssBaseline } from '@mui/material';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRoutes } from 'react-router-dom';
import router from 'src/router';
import './index.css';

// Redux
import type { RootState } from './redux/store';
import { setTermModal, setOrbitAISnackBar } from './redux/siteAuditReducer';

// Providers / theme
import ThemeProvider from './theme/ThemeProvider';
import { ErrorBoundary } from './common/ErrorBoundary';

// UI components
import { CommonSnackBar } from './common/CommonSnackBar';
import OrbitAISnackbar from './content/orbitAi/Components/OrbitAISnackbar';
import UpgradeNewPlanModal from './components/SubScription/UpgradeNewPlanModal';
import Intercom from 'react-intercom';
import { INTERCOM_APP_ID } from 'src/appConfig';

// Hooks
import { useUserDetails } from './hooks/apiHooks/useUserDetails';
import { useIntercomUserData } from './hooks/useIntercomUserData';
import { useLogout } from './hooks/useLogout';
import { useAppInitializer } from './hooks/useAppInitializer';
import { useSSEManager } from './hooks/useSSEManager';
import { useAnalyticsSetup } from './hooks/useAnalyticsSetup';
import { useCookieSync } from './hooks/useCookieSync';
import { useIntercomVisibility } from './hooks/useIntercomVisibility';
import { usePricingModal } from './hooks/usePricingModal';
import { localSeoSignupSourceSqsp, localseoSignupSqspFlow } from 'src/common/config/TextMessage';
import { getCookie } from 'src/common/config/FunctionHelper';
import { PostHogPageViewTracker } from './components/analytics/PostHogPageViewTracker';

// ---------------------------------------------------------------------------

const AppContent = () => {
  const dispatch = useDispatch();
  const content = useRoutes(router);
  const { pathname } = useLocation();

  // ── Core hooks ────────────────────────────────────────────────────────────
  const logout = useLogout();
  const { userId } = useAppInitializer({ logout });
  useSSEManager();
  useAnalyticsSetup();
  useCookieSync();
  useIntercomVisibility(pathname);

  const {
    showPricingModal,
    isPricingModalLoading,
    activePlanName,
    planDetails,
    handleStayOnCurrentPlan,
    handleUpgrade
  } = usePricingModal();

  // ── Selectors needed for rendering ───────────────────────────────────────
  const openSnackBar = useSelector((state: RootState) => state.siteAudit.openSnackBar) || false;
  const orbitAISnackBar = useSelector((state: RootState) => state.siteAudit.orbitAISnackBar);
  const userDetails: any = useSelector((state: RootState) => state.counter.userDetails);
  const isLocalSEOAddonBuy = useSelector((state: RootState) => state.counter.isLocalSEOAddonBuy);
  const isUpgradedToSeo = useSelector((state: RootState) => state.localSeo.isUpgradedToSeo);
  const isLocalSeoExtResultAccessed = useSelector((state: RootState) => state.localSeo.isLocalSeoExtResultAccessed);
  const isAiAddonPurchased = useSelector((state: RootState) => state.dashboard.isAiAddonPurchased);
  const isAiVisibilityVisit = useSelector((state: RootState) => state.dashboard.isAiVisibilityVisit);
  const isAiScoreView = useSelector((state: RootState) => state.dashboard.isAiScoreView);

  // ── Intercom user data ────────────────────────────────────────────────────
  const isLocalSeoSqSp =
    (getCookie(localseoSignupSqspFlow) ?? null) === localSeoSignupSourceSqsp;

  const { userData, setUserData } = useIntercomUserData({
    userDetails,
    isLocalSEOAddonBuy,
    isUpgradedToSeo,
    isAiAddonPurchased,
    isAiVisibilityVisit,
    isAiScoreView,
    isLocalSeoSqSp,
    isLocalSeoExtResultAccessed
  });

  // ── User details API — triggered once userId is resolved ─────────────────
  const { data: userDetailsResponse, isError } = useUserDetails({
    enabled: !!userId && !userDetails?.email
  });

  useEffect(() => {
    if (userDetailsResponse) {
      const isLocalSeo = userDetailsResponse?.isLocalSeo || false;
      dispatch(setTermModal(userDetailsResponse?.has_agreed_to_terms));
      setUserData({
        name: userDetailsResponse?.name,
        email: userDetailsResponse?.email,
        is_plugin_installed: !!document.querySelector('#rough-water-seo-root'),
        user_hash: userDetailsResponse?.hash,
        localextension: isLocalSeo ? 'yes' : 'no'
      });
    } else if (isError) {
      logout();
    }
  }, [userDetailsResponse, isError, setUserData, logout, dispatch]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <CommonSnackBar open={openSnackBar} />
      <OrbitAISnackbar
        open={Boolean(orbitAISnackBar)}
        onClose={() => dispatch(setOrbitAISnackBar(null))}
        status={orbitAISnackBar?.status || 'error'}
        message={orbitAISnackBar?.message}
      />
      {content}
      {userDetails?.email && <Intercom appID={INTERCOM_APP_ID} {...userData} />}
      {activePlanName && planDetails && showPricingModal && (
        <UpgradeNewPlanModal
          showPricingModal={showPricingModal}
          handleStayOnCurrentPlan={handleStayOnCurrentPlan}
          handleUpgrade={handleUpgrade}
          loading={isPricingModalLoading}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------

const App = () => (
  <ThemeProvider>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <CssBaseline />
      <PostHogPageViewTracker />
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </LocalizationProvider>
  </ThemeProvider>
);

export default App;
